import React, { FC } from 'react'
import { TertiaryButton } from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { styled } from '@moonpig/launchpad-utils'
import { spacing } from '@moonpig/launchpad-theme'
import { useOptionalSearchStore } from '../../../store/SearchStore'
import { getClearFiltersEventLabel } from '../../../store/helpers'
import { clearFiltersEvent } from '../../../pages/SearchPage/events'
import {
  useSearchAnalytics,
  sanitizeContentType,
} from '../../../pages/SearchPage/analytics'
import { FilterCategory, IntegerFilter } from '../../../services/types/services'
import { FilterSource } from '../../../types'
import { useFindLocaleText } from '../../../text-localisation'
import { ClearFiltersProps } from '../../../store/slices/filters'

const StyledButton = styled(TertiaryButton)`
  ${s({
    typography: 'typeButtonLabel',
    color: 'colorInteractionTextLink',
    py: 5,
    px: 2,
    textAlign: 'right',
  })}
`
const StyledWrapper = styled.div`
  min-width: ${spacing(12)}px;
`

type Props = {
  level: number
  category?: FilterCategory | IntegerFilter
  type?: 'filters' | 'sort'
  hide: boolean
  source: FilterSource
  clearFilters: ({ source, category, type }: ClearFiltersProps) => void
}

export const ClearButton: FC<Props> = ({
  level,
  category,
  type = 'filters',
  source,
  hide,
  clearFilters,
}) => {
  const isLaunchpadAnalytics = useSearchAnalytics()

  const pageType = useOptionalSearchStore(
    store => store.pageContext.type,
    'search',
  )

  const clearLabel = getClearFiltersEventLabel([
    level === 0 ? { source } : { category, type, source },
  ])

  // Legacy event: apps/explore/find/src/store/middleware/trackEvent.ts
  const clearClickEvent = clearFiltersEvent()
    .withEventData({
      category: `${pageType} action`.toLowerCase(),
      label: clearLabel.toLowerCase(),
    })
    .withContentData({
      content_type: sanitizeContentType(
        `${pageType} action | clear filters | ${clearLabel}`,
      ),
    })

  const localiseText = useFindLocaleText()

  const onClear = () => {
    if (level === 0) {
      clearFilters({ source })
    } else {
      clearFilters({ category, type, source })
    }
  }

  return (
    <StyledWrapper>
      {!hide && (
        <StyledButton
          aria-label={localiseText('find.clear_selection')}
          events={
            isLaunchpadAnalytics ? { onClick: clearClickEvent } : undefined
          }
          onClick={() => onClear()}
        >
          {localiseText('find.clear')}
        </StyledButton>
      )}
    </StyledWrapper>
  )
}
